import { get, set, del } from "idb-keyval";
import type { Persister, PersistedClient } from "@tanstack/react-query-persist-client";
import type { Query, QueryKey } from "@tanstack/react-query";
import { queryClient } from "./queryClient";
import { queryKeys } from "./utils/queryKeys";

/**
 * The React Query cache, persisted to IndexedDB so a return visit paints the signed-in home from disk, then
 * revalidates (cache-then-revalidate). Three rules, each from a measured problem (2026-10-13):
 *
 * 1. **It belongs to one account.** The blob is stored with the Clerk user id that wrote it and restored only
 *    for that id; CacheOwnerGuard wipes it on sign-out or when a different account signs in. Before, a second
 *    person signing in on the same browser could see the first one's games until the refetch landed.
 * 2. **It holds what the home needs to paint, nothing heavier**: the home's games list, the newest games' full
 *    records, the Openings tile's Insights (#339) and the profile. It used to hold every games list, including whole histories (24 MB + 4 MB for a
 *    4,222-game account), and nothing could fetch until that blob was read back. The Library's first page is kept too
 *    (#351: its newest 50 games' card rows, ~60 KB, never the history behind them), and its newest game's record, the
 *    panel's default: the home's newest game when both are cached, so no more on disk.
 * 3. **Writes are throttled**: the persister saved on every cache event, structured-cloning the whole blob on
 *    the main thread each time.
 */
const IDB_KEY = "chess-deck-rq-cache";
const OWNER_KEY = "chess-deck-cache-owner";
const SAVE_THROTTLE_MS = 1000;
/** How many of the home's newest games keep their full record on disk: the last game's graph, and the newest
 *  mistakes' coach lines. Each record is ~100-200 KB. */
const HOME_RECORDS = 3;

/** The account whose cache is on disk (localStorage: read synchronously, before Clerk loads). */
export const cacheOwner = {
  get: (): string | null => {
    try { return localStorage.getItem(OWNER_KEY); } catch { return null; }
  },
  set: (userId: string): void => {
    try { localStorage.setItem(OWNER_KEY, userId); } catch { /* private mode: no persistence, nothing to own */ }
  },
  clear: (): void => {
    try { localStorage.removeItem(OWNER_KEY); } catch { /* as above */ }
  },
};

export type CacheAction = "wait" | "keep" | "adopt" | "wipe" | "wipe-and-adopt";

/** What to do with the cache once Clerk says who's signed in. A first sign-in on this browser only adopts it:
 *  nothing on disk belongs to anyone (restore returned nothing), so there's nothing to wipe. */
export function cacheAction(auth: { readonly isLoaded: boolean; readonly userId: string | null }, owner: string | null): CacheAction {
  if (!auth.isLoaded) return "wait";
  if (auth.userId) return auth.userId === owner ? "keep" : owner ? "wipe-and-adopt" : "adopt";
  return owner ? "wipe" : "keep";
}

interface OwnedBlob {
  readonly owner: string;
  readonly client: PersistedClient;
}

/** A persister that writes at most once per `throttleMs` (the last state wins), tags the blob with its owner,
 *  and restores it only for that owner. `owner` is read at write and restore time. */
export function createOwnedPersister(owner: () => string | null, throttleMs: number): Persister {
  let pending: PersistedClient | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = async () => {
    timer = null;
    const client = pending;
    const who = owner();
    pending = null;
    if (client && who) await set(IDB_KEY, { owner: who, client } satisfies OwnedBlob);
  };
  return {
    persistClient: (client) => {
      pending = client;
      if (!timer) timer = setTimeout(() => { void flush(); }, throttleMs);
    },
    restoreClient: async () => {
      const who = owner();
      const blob = await get<OwnedBlob>(IDB_KEY);
      return who && blob && blob.owner === who ? blob.client : undefined;
    },
    removeClient: async () => {
      pending = null;
      if (timer) { clearTimeout(timer); timer = null; }
      await del(IDB_KEY);
    },
  };
}

export const idbPersister = createOwnedPersister(cacheOwner.get, SAVE_THROTTLE_MS);

/** The Openings tile's Insights (#339, #362): the combined last-100 read, the rail's counts. Not the tile's year of games,
 *  nor the other combined reads (a family over a window, a comparison's sources, one game's). */
const HOME_INSIGHTS = queryKeys.openingCombined();

const LIBRARY_FIRST = queryKeys.gamesCardFirst;
const isKey = (key: QueryKey, want: readonly unknown[]) => key.length === want.length && want.every((k, i) => key[i] === k);

/** Is this query worth putting on disk? `homeIds`: the games whose full records the home (or the Library) shows first. */
export function persistableQuery(key: QueryKey, homeIds: ReadonlySet<string>): boolean {
  if (key[0] === "games") return key[1] === "since" || isKey(key, HOME_INSIGHTS) || isKey(key, LIBRARY_FIRST);
  if (key[0] === "userProfile") return true;
  if (key[0] === "analysis") return typeof key[1] === "string" && homeIds.has(key[1]);
  return false;
}

type Listed = { games?: ReadonlyArray<{ gameOriginId?: string }> };

/** The newest games in the home's list (the ones whose full records it shows first), and the Library's newest (#351). */
function homeGameIds(): Set<string> {
  const lists = queryClient.getQueriesData<Listed>({ queryKey: ["games", "since"] });
  const ids = new Set<string>();
  for (const [, data] of lists) {
    for (const g of (data?.games ?? []).slice(0, HOME_RECORDS)) if (g.gameOriginId) ids.add(g.gameOriginId);
  }
  const newest = queryClient.getQueryData<Listed>(LIBRARY_FIRST)?.games?.[0]?.gameOriginId;
  if (newest) ids.add(newest);
  return ids;
}

export function shouldDehydrateQuery(query: Query): boolean {
  return query.state.status === "success" && persistableQuery(query.queryKey, homeGameIds());
}
